import { useState, useMemo } from 'react';
import {useDispatch, useSelector} from 'react-redux';
import { useToast } from 'react-native-toast-notifications';
import usePermissions from "../services/Permissions";
import {fetchApi} from "../services/HTTP";
import {hideLoader, showLoader} from "../redux/rootManagerSlice";
import { t } from '../services/i18n';
import {isNonGoogleEmail} from "../services/Helpers";
import {toggleScheduleLoader} from "../redux/ScheduleSlice";


const useGoogleCalendarSync = ({ onConnectSuccess, onDisconnectSuccess } = {}) => {
    const toast = useToast();
    const { hasLock } = usePermissions();
    const dispatch = useDispatch();
    const user = useSelector(state => state.user.user);
    const [confirm, setConfirm] = useState(null);

    const hasNonGoogleEmail = useMemo(() => isNonGoogleEmail(user?.email), [user?.email]);

    const disableConnection = useMemo(() =>
        hasLock('calendarSync') || !user.email || hasNonGoogleEmail || user.pending
    , [hasLock, user.email, hasNonGoogleEmail, user.pending]);

    const disableReason = useMemo(() => {
        if (!user.email) return t('error-no-email-for-google-sync');
        if (hasNonGoogleEmail) return t('error-non-google-email-domain');
        return null;
    }, [user.email, hasNonGoogleEmail]);

    const connectToGoogleCalendar = () => {
        console.log('process.env.SYNC_GOOGLE_AUTH_CLIENT_ID', process.env.SYNC_GOOGLE_AUTH_CLIENT_ID)
        var SCOPES = 'https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email';
        const client = window.google.accounts.oauth2.initCodeClient({
            client_id: process.env.SYNC_GOOGLE_AUTH_CLIENT_ID,
            scope: SCOPES,
            ux_mode: 'popup',
            callback: async (response) => {
                try {
                    dispatch(toggleScheduleLoader(true));
                    const res = await fetchApi('calendarSyncConnections/syncCalendar', 'POST', { code: response.code }, false, true);
                    dispatch(toggleScheduleLoader(false));
                    if (res.error || !res.status || res.status !== 'active') {
                        toast.show(t(res.error_code || 'error-connect-calendar-failed'), { type: 'danger', duration: 3000 });
                        return;
                    }
                    toast.show(t('success-connect-calendar'), { type: 'success', duration: 2000 });
                    if (onConnectSuccess) onConnectSuccess(res);
                } catch (error) {
                    dispatch(toggleScheduleLoader(false));
                    if(error.messageToUser) {
                        toast.show(t(error.messageToUser), { type: 'danger', duration: 3000 });
                    } else {
                        toast.show(t('error-connect-calendar-network'), { type: 'danger', duration: 3000 });
                    }
                }
            },
            error_callback: () => {
                toast.show(t('error-google-oauth-failed'), { type: 'danger', duration: 3000 });
            }
        });
        client.requestCode();
    };

    const showConfirmDeleteSync = () => {
        setConfirm({
            header: t('delete-google-sync-header'),
            subheader: t('delete-google-sync-subheader'),
            confirmText: t('confirm'),
            cancelText: t('cancel'),
            onConfirm: deleteConnectionToGoogleCalendar,
            confirmLoading: false,
        });
    };

    const deleteConnectionToGoogleCalendar = async () => {
        try {
            setConfirm(prev => ({ ...prev, confirmLoading: true }));
            const res = await fetchApi('calendarSyncConnections/disconnectCalendar', 'POST', null, false, true);
            if (onDisconnectSuccess) {
                onDisconnectSuccess(res);
            }
            setConfirm(null);
        } catch (error) {
            console.error('Error disconnecting Google Calendar:', error);
            setConfirm(prev => ({ ...prev, confirmLoading: false }));
        }
    };

    return {
        confirm,
        user,
        hasNonGoogleEmail,
        disableConnection,
        disableReason,
        connectToGoogleCalendar,
        showConfirmDeleteSync,
        deleteConnectionToGoogleCalendar,
        setConfirm,
    };
};

export default useGoogleCalendarSync;
